import LocalStorageService from "service/localStorageService";
import { websocketCheck } from "./websocketQuery";

const APP_DASHBOARD_API = process.env.REACT_APP_DASHBOARD_API;
// Change the protocol to wss
const WS_DASHBOARD_API = APP_DASHBOARD_API?.replace("https", "wss");

export class HeatmapQuery {
    private connectionPromise: Promise<WebSocket> | null = null;
    private connectionStatus: "connecting" | "connected" | "disconnected" =
        "disconnected";
    private wsInstance: WebSocket | null = null;
    private orgId: string | null = null;

    private chartInstances: Record<
        string,
        {
            timer?: NodeJS.Timeout | null;
            connected?: boolean;
            onNewSession?: (sessionId: string) => void;
            onNewData?: (data: any[]) => void;
        }
    > = {};

    static cleanUpWS(wsInstance: WebSocket | null) {
        if (wsInstance) {
            wsInstance.close();
            wsInstance.onopen = null;
            wsInstance.onmessage = null;
            wsInstance.onerror = null;
            wsInstance.onclose = null;
        }
    }

    private clearConnection() {
        if (this.wsInstance) {
            HeatmapQuery.cleanUpWS(this.wsInstance);
            this.wsInstance = null;
        }
        this.connectionStatus = "disconnected";
        this.connectionPromise = null;
        this.orgId = null;

        this.chartInstances = {};
    }

    private getCurrentUserInfo() {
        const orgId = LocalStorageService.getItem("orgId");
        const { AccessToken } = LocalStorageService.getItem("tokens");

        return {
            orgId,
            accessToken: AccessToken,
        };
    }

    private async generateAccessToken() {
        try {
            await websocketCheck();
        } catch (error) {
            // Intentionally left empty
        }
    }

    initConnection(): Promise<WebSocket> {
        // Reset the connection if the orgId has changed
        const { orgId, accessToken } = this.getCurrentUserInfo();
        if (this.orgId !== orgId) {
            this.clearConnection();
        }

        if (this.connectionPromise) {
            return this.connectionPromise;
        }

        this.connectionStatus = "connecting";
        this.orgId = orgId;

        this.connectionPromise = new Promise(async (resolve, reject) => {
            // Generate access token to ensure the token is valid before connecting
            await this.generateAccessToken();

            // Initialize WebSocket connection
            let websocketUrl = `${WS_DASHBOARD_API}/ws/stream/${orgId}`;
            websocketUrl += `?token=${encodeURIComponent(accessToken)}`;
            const ws = new WebSocket(websocketUrl);

            ws.onopen = () => {
                this.connectionStatus = "connected";
                this.connectionPromise = null;
                resolve(ws);
            };

            ws.onmessage = (event: MessageEvent) => {
                const message = JSON.parse(event.data);

                if (message.add) {
                    // Handle the added message
                    const sessionId = message.id;
                    this.handleAddSession(sessionId);
                    return;
                }

                if (message.data || message.error) {
                    // Handle the new data message
                    const sessionId = message.id;
                    const chartInstance = this.chartInstances[sessionId];
                    if (
                        chartInstance &&
                        typeof chartInstance.onNewData === "function"
                    ) {
                        chartInstance?.onNewData(message);
                    }
                }
            };

            ws.onclose = () => {
                this.clearConnection();
                reject();
            };

            this.wsInstance = ws;
        });

        return this.connectionPromise;
    }

    isReady(): boolean {
        const { orgId } = this.getCurrentUserInfo();
        if (!orgId || this.orgId !== orgId) {
            return false;
        }

        return (
            this.connectionStatus === "connected" && this.wsInstance !== null
        );
    }

    handleAddSession(sessionId: string): void {
        if (!this.chartInstances[sessionId]) {
            this.chartInstances[sessionId] = {};
        }

        const chartInstance = this.chartInstances[sessionId];
        chartInstance.connected = true;

        if (typeof chartInstance.onNewSession === "function") {
            chartInstance.onNewSession(sessionId);
        }
    }

    createSession(sessionId: string, gatewayIds: string[]): Promise<void> {
        if (!this.chartInstances[sessionId]) {
            this.chartInstances[sessionId] = {
                connected: false,
            };
        }

        const payload = {
            add: gatewayIds,
            id: sessionId,
        };

        return new Promise((resolve, reject) => {
            this.chartInstances[sessionId].onNewSession = (
                sessionId: string
            ) => {
                resolve();
            };

            this.wsInstance?.send(JSON.stringify(payload));
        });
    }

    closeConnection(sessionId: string): void {
        if (this.chartInstances[sessionId]) {
            delete this.chartInstances[sessionId];
        }

        if (Object.keys(this.chartInstances).length === 0) {
            HeatmapQuery.cleanUpWS(this.wsInstance);
            this.wsInstance = null;
            this.connectionStatus = "disconnected";
            this.connectionPromise = null;
        }
    }

    async fetchData(
        sessionId: string,
        gatewayIds: string[],
        timeoutMs: number = 5000
    ): Promise<any[]> {
        // If there is a pending connection, wait for it to resolve
        if (!this.isReady() && this.connectionPromise) {
            await this.initConnection();
        }

        if (!this.chartInstances[sessionId]?.connected) {
            await this.createSession(sessionId, gatewayIds);
        }

        const payload = {
            update: sessionId,
        };

        return new Promise((resolve, reject) => {
            let timeoutId: any = null;
            let isResolved = false;

            // Set up timeout
            timeoutId = setTimeout(() => {
                if (!isResolved) {
                    isResolved = true;
                    reject(
                        new Error(
                            `Timeout: No response received within ${timeoutMs}ms for session ${sessionId}`
                        )
                    );
                }
            }, timeoutMs);

            this.chartInstances[sessionId].onNewData = (
                message: any | { error: string }
            ) => {
                if (isResolved) return; // Prevent multiple resolves/rejects

                isResolved = true;
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }

                if (message.error) {
                    return reject({ error: message.error });
                }

                resolve(message.data);
            };

            this.wsInstance?.send(JSON.stringify(payload));
        });
    }
}

export default HeatmapQuery;
